import { Formik } from 'formik';
import classNames from 'classnames';
import React, { useState } from 'react';

import LFModal from 'components/common/LFModal';
import Loading from 'components/common/loading/Loading';
import InputWrapper from 'components/common/inputWrapper';
import Input from 'components/common/inputWrapper/components/Input';

import { ADD_CATEGORY, EDIT_CATEGORY } from 'constants/skills';

import { addCategorySkillSchema } from 'schemas/addSkillCategorySchema';

import {
  useCreateSkillV2CategoryMutation,
  useDeleteSkillV2CategoryMutation,
  useUpdateSkillV2CategoryMutation,
} from 'hooks/query/useSkillQueries';

import { success } from 'utils/toast';
import { handleError } from 'utils/errorHandler';

type SkillCategoryNode = {
  id: number;
  name: string;
  description?: string;
  subSkills?: Array<{ id?: number | string }>;
};

type FormValues = {
  name: string;
  description: string;
};

interface Props {
  categoryData?: SkillCategoryNode | null;
  isOpen: boolean;
  onClose: () => void;
  callback?: (result?: { message?: string }) => void;
}

const AddSkillCategoryModal: React.FC<Props> = (props) => {
  const { categoryData, isOpen, onClose, callback } = props;

  const { mutateAsync: createCategory, isPending: isCreating } = useCreateSkillV2CategoryMutation();
  const { mutateAsync: updateCategory, isPending: isUpdating } = useUpdateSkillV2CategoryMutation();
  const { mutate: deleteCategory, isPending: isDeletingCategory } = useDeleteSkillV2CategoryMutation();

  const isSubmitting = isCreating || isUpdating;
  const isDeleting = isDeletingCategory;

  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);

  const resetModalState = () => {
    setIsDeleteConfirmationModalOpen(false);
  };

  const handleClose = () => {
    resetModalState();
    onClose();
  };

  const initialValues: FormValues = {
    name: categoryData?.name || '',
    description: categoryData?.description || '',
  };

  const isEditMode = Boolean(categoryData);

  const handleSubmit = async (body: FormValues) => {
    if (isSubmitting) {
      return;
    }

    try {
      if (!isEditMode) {
        const data = (await createCategory(body)) as { message?: string };

        callback?.(data);
        handleClose();
        success({ title: 'Success', message: data.message });
      } else {
        const data = (await updateCategory({ categoryId: categoryData!.id, body })) as { message?: string };

        callback?.(data);
        handleClose();
        success({ title: 'Success', message: data.message });
      }
    } catch (err) {
      handleError(err);
    }
  };

  const TriggerBtn = () => {
    return <div className="hidden"></div>;
  };

  const showDeleteConfirmationModal = () => {
    setIsDeleteConfirmationModalOpen(true);
  };

  const closeDeleteConfirmationModal = () => {
    setIsDeleteConfirmationModalOpen(false);
  };

  const onDelete = () => {
    if (!categoryData) {
      return;
    }

    deleteCategory(categoryData.id, {
      onSuccess: () => {
        success({
          title: 'Success',
          message: 'The category has been deleted successfully.',
        });

        callback?.();
      },
      onSettled: () => {
        handleClose();
      },
    });
  };

  return (
    <LFModal
      TriggerBtn={TriggerBtn}
      isOpen={isOpen}
      onClose={handleClose}
      closeButton={true}
      title={{ text: isEditMode ? EDIT_CATEGORY : ADD_CATEGORY, type: 'info' }}
      className="skill-modal"
      htmlContent={
        <Formik<FormValues>
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={addCategorySkillSchema}
        >
          {({
            handleSubmit,
            handleBlur,
            handleChange,
            errors,
            values,
            dirty,
            setFieldValue,
            touched,
            setFieldTouched,
          }) => {
            return (
              <form onSubmit={handleSubmit} className="skill-modal__form">
                <div className="skill-modal__form-body">
                  <Input
                    isMandatory
                    label="Category Name"
                    name="name"
                    placeholder="Type category name"
                    className="wp-100"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name ? errors.name : undefined}
                    disabled={isSubmitting}
                    multiline={false}
                    type="text"
                  />

                  <InputWrapper label="Description" error={touched.description ? errors.description : undefined}>
                    <textarea
                      name="description"
                      placeholder="Type category description here..."
                      className={classNames('form-elem custom-scroll-bar skill-modal__textarea', {
                        'form-elem--error': Boolean(touched.description && errors.description),
                      })}
                      value={values.description}
                      onChange={(e) => {
                        setFieldTouched('description', true, true);
                        setFieldValue('description', e.target.value);
                      }}
                      disabled={isSubmitting}
                    />
                  </InputWrapper>
                </div>

                <div className="skill-modal__footer">
                  <button
                    type="submit"
                    className={classNames('btn skill-modal__btn', {
                      'btn--primary': !isSubmitting && dirty,
                      'btn--disabled': isSubmitting || !dirty,
                    })}
                    disabled={isSubmitting || !dirty}
                  >
                    <span className={classNames({ invisible: isSubmitting })}>{isEditMode ? 'Save' : 'Add'}</span>
                    {isSubmitting && <Loading />}
                  </button>

                  <button
                    type="button"
                    className={classNames('btn ml-10 skill-modal__btn', {
                      'btn--outlined-grey': !isSubmitting,
                      'btn--disabled': isSubmitting,
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      handleClose();
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>

                  {isEditMode && !(categoryData?.subSkills?.length || 0) && (
                    <button
                      type="button"
                      className="btn btn--outlined-danger f-right card-button change-tentative-exit__btn"
                      onClick={showDeleteConfirmationModal}
                      disabled={isSubmitting}
                    >
                      Delete
                    </button>
                  )}
                </div>

                {isDeleteConfirmationModalOpen && (
                  <div className="skill-delete-modal absolute">
                    <div className="font-14">
                      <p>
                        {'Are you sure you want to delete the category: '} <b>{values.name}</b>
                        {'?'}
                      </p>
                      <p>{'It can not be undone once deleted.'}</p>
                    </div>
                    <div className="d-flex gap-4x mt-4 f-right">
                      <button
                        type="button"
                        className={classNames('btn ml-10 skill-modal__btn', {
                          'btn--outlined-grey': !isSubmitting,
                          'btn--disabled': isSubmitting,
                        })}
                        onClick={closeDeleteConfirmationModal}
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn--outlined-danger f-right card-button min-width-80"
                        onClick={onDelete}
                        disabled={isDeleting}
                      >
                        {isDeleting ? <Loading /> : 'Yes, delete'}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            );
          }}
        </Formik>
      }
    />
  );
};

export default AddSkillCategoryModal;
